import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AssinanteForm } from "./AssinanteForm";
import { Empresa, EmpresaUpdate } from "../../types/empresa"; // Certifique-se que este tipo existe
import { empresaService } from "../../services/empresaService";

export const EditarAssinante: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState<Empresa | null>(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedPlanoId, setSelectedPlanoId] = useState<number | null>(null); // New state for selected plan

  useEffect(() => {
    if (id) {
      empresaService
        .detalhar(Number(id))
        .then((data: Empresa) => {
          setInitialData(data);
          setSelectedPlanoId(data.plano?.id || null); // Initialize selectedPlanoId from data.plano
          setLoading(false);
        })
        .catch((error: any) => {
          console.error("Erro ao buscar dados do assinante:", error);
          setErrorMessage("Erro ao carregar dados do assinante.");
          setLoading(false);
        });
    }
  }, [id, navigate]);

  const handleSubmit = async (data: Partial<EmpresaUpdate>) => {
    if (!id) return;
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const dataToSubmit: Partial<EmpresaUpdate> = { ...data };
      if (selectedPlanoId !== null) {
        dataToSubmit.plano = selectedPlanoId; // Include selected plan ID as 'plano'
      }
      
      await empresaService.atualizar(Number(id), dataToSubmit);
      setSuccessMessage("Assinante atualizado com sucesso!");
      // Aguarda 2 segundos para mostrar mensagem antes de redirecionar
      setTimeout(() => {
        navigate("/assinantes");
      }, 2000);
    } catch (error: any) {
      console.error("Erro ao atualizar assinante:", error);
      const mensagemErro =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Ocorreu um erro ao atualizar o assinante.";
      setErrorMessage(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Carregando...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        Editar Assinante
      </h1>

      {/* Banner de Sucesso */}
      {successMessage && (
        <div
          className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg animate-pulse"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-green-600 text-2xl flex-shrink-0">✅</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-green-800 mb-1">
                Sucesso!
              </h4>
              <p className="text-sm text-green-700">{successMessage}</p>
              <p className="text-xs text-green-600 mt-1">Redirecionando...</p>
            </div>
          </div>
        </div>
      )}

      {/* Banner de Erro ao Carregar */}
      {errorMessage && !initialData && (
        <div
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-800 mb-1">
                Erro ao Carregar
              </h4>
              <p className="text-sm text-red-700">{errorMessage}</p>
              <button
                onClick={() => navigate("/assinantes")}
                className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
              >
                Voltar para lista de assinantes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner de Erro ao Salvar */}
      {errorMessage && initialData && (
        <div
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-800 mb-1">
                Erro ao Salvar
              </h4>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-600 hover:text-red-800 text-xl leading-none flex-shrink-0"
              aria-label="Fechar mensagem"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="bg-white p-8 rounded-lg shadow-md">
        {initialData && (
          <AssinanteForm
            onSubmit={handleSubmit}
            initialData={initialData}
            isEdit
            onPlanSelected={setSelectedPlanoId} // Pass setSelectedPlanoId here
          />
        )}
      </div>
    </div>
  );
};
